import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const Sidebar = ({ isCollapsed = false, onToggleCollapse, className = '' }) => {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Navigation items with hardcoded data
  const navigationItems = [
    {
      id: 'trading',
      label: 'Trading',
      path: '/main-trading-dashboard',
      icon: 'TrendingUp',
      tooltip: 'Real-time trading dashboard and position management'
    },
    {
      id: 'analytics',
      label: 'Analytics',
      path: '/portfolio-analytics',
      icon: 'BarChart3',
      tooltip: 'Portfolio performance analysis and insights'
    },
    {
      id: 'market-analysis',
      label: 'Market Analysis',
      path: '/market-analysis-hub',
      icon: 'LineChart',
      tooltip: 'Advanced charting and technical analysis tools'
    },
    {
      id: 'risk-management',
      label: 'Risk Management',
      path: '/risk-management-center',
      icon: 'Shield',
      tooltip: 'Risk monitoring and exposure management'
    },
    {
      id: 'account',
      label: 'Account',
      path: '/account-management',
      icon: 'User',
      tooltip: 'Account settings and transaction history'
    }
  ];

  // Close mobile sidebar when route changes
  useEffect(() => {
    setIsMobileOpen(false);
  }, [location?.pathname]);

  // Handle navigation
  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileOpen(false);
  };

  // Check if current path is active
  const isActiveRoute = (path) => {
    return location?.pathname === path;
  };

  const sidebarWidth = isCollapsed ? 'w-16' : 'w-60';

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className={`hidden lg:flex lg:fixed lg:inset-y-0 lg:z-sidebar ${sidebarWidth} transition-all duration-300 ease-in-out ${className}`}>
        <div className="flex flex-col flex-1 bg-card border-r border-border shadow-trading">
          {/* Sidebar Header */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-border">
            {!isCollapsed && (
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                  <Icon name="TrendingUp" size={20} color="white" />
                </div>
                <span className="text-lg font-semibold text-foreground">Zonex Trader</span>
              </div>
            )}
            {isCollapsed && (
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center mx-auto">
                <Icon name="TrendingUp" size={20} color="white" />
              </div>
            )}
            {onToggleCollapse && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onToggleCollapse}
                iconName={isCollapsed ? "ChevronRight" : "ChevronLeft"}
                className="w-8 h-8 p-0"
              />
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-2">
            {navigationItems?.map((item) => (
              <div key={item?.id} className="relative group">
                <button
                  onClick={() => handleNavigation(item?.path)}
                  className={`nav-item w-full ${
                    isActiveRoute(item?.path) ? 'nav-item-active' : 'nav-item-inactive'
                  } ${isCollapsed ? 'justify-center px-2' : 'justify-start px-3'}`}
                  title={isCollapsed ? item?.tooltip : ''}
                >
                  <Icon 
                    name={item?.icon} 
                    size={20} 
                    className={isCollapsed ? '' : 'mr-3'} 
                  />
                  {!isCollapsed && (
                    <span className="text-sm font-medium">{item?.label}</span>
                  )}
                </button>

                {/* Tooltip for collapsed state */}
                {isCollapsed && (
                  <div className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 px-2 py-1 bg-popover text-popover-foreground text-xs rounded-md shadow-trading-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-dropdown">
                    {item?.label}
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Sidebar Footer */}
          <div className="p-4 border-t border-border">
            <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'}`}>
              {!isCollapsed && (
                <div className="text-xs text-muted-foreground">
                  <div>Market Status: Open</div>
                  <div className="font-mono">NYSE: 09:30 - 16:00 EST</div>
                </div>
              )}
              <div className={`flex ${isCollapsed ? 'flex-col space-y-2' : 'space-x-2'}`}>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="HelpCircle"
                  className="w-8 h-8 p-0"
                  title="Help & Support"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Bell"
                  className="w-8 h-8 p-0"
                  title="Notifications"
                />
              </div>
            </div>
          </div>
        </div>
      </aside>
      {/* Mobile Sidebar Toggle */}
      <div className="lg:hidden fixed top-4 left-4 z-mobile-nav">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsMobileOpen(true)}
          iconName="Menu"
          className="w-10 h-10 p-0 bg-card shadow-trading"
        />
      </div>
      {/* Mobile Sidebar Overlay */}
      {isMobileOpen && (
        <div className="lg:hidden fixed inset-0 z-modal">
          <div 
            className="absolute inset-0 bg-black/50" 
            onClick={() => setIsMobileOpen(false)}
          />
          <aside className="absolute left-0 top-0 bottom-0 w-64 bg-card shadow-trading-lg animate-fade-in">
            <div className="flex flex-col h-full">
              {/* Mobile Header */}
              <div className="flex items-center justify-between h-16 px-4 border-b border-border">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                    <Icon name="TrendingUp" size={20} color="white" />
                  </div>
                  <span className="text-lg font-semibold text-foreground">Zonex Trader</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsMobileOpen(false)}
                  iconName="X"
                  className="w-8 h-8 p-0"
                />
              </div>

              {/* Mobile Navigation */}
              <nav className="flex-1 px-4 py-6 space-y-2">
                {navigationItems?.map((item) => (
                  <button
                    key={item?.id}
                    onClick={() => handleNavigation(item?.path)}
                    className={`nav-item w-full justify-start px-3 ${
                      isActiveRoute(item?.path) ? 'nav-item-active' : 'nav-item-inactive'
                    }`}
                  >
                    <Icon name={item?.icon} size={20} className="mr-3" />
                    <span className="text-sm font-medium">{item?.label}</span>
                  </button>
                ))}
              </nav>

              {/* Mobile Footer */}
              <div className="p-4 border-t border-border">
                <div className="text-xs text-muted-foreground mb-3">
                  <div>Market Status: Open</div>
                  <div className="font-mono">NYSE: 09:30 - 16:00 EST</div>
                </div>
                <div className="flex space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="HelpCircle"
                    className="flex-1"
                  >
                    Help
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Bell"
                    className="flex-1"
                  >
                    Alerts
                  </Button>
                </div>
              </div>
            </div>
          </aside>
        </div>
      )}
      {/* Mobile Bottom Navigation */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-card border-t border-border z-mobile-nav">
        <div className="flex">
          {navigationItems?.slice(0, 4)?.map((item) => (
            <button
              key={item?.id}
              onClick={() => handleNavigation(item?.path)}
              className={`flex-1 flex flex-col items-center py-2 px-1 ${
                isActiveRoute(item?.path) 
                  ? 'text-primary' :'text-muted-foreground hover:text-foreground'
              } transition-colors duration-200`}
            >
              <Icon name={item?.icon} size={20} />
              <span className="text-xs mt-1 font-medium">{item?.label}</span>
            </button>
          ))}
          <button
            onClick={() => handleNavigation('/account-management')}
            className={`flex-1 flex flex-col items-center py-2 px-1 ${
              isActiveRoute('/account-management') 
                ? 'text-primary' :'text-muted-foreground hover:text-foreground'
            } transition-colors duration-200`}
          >
            <Icon name="User" size={20} />
            <span className="text-xs mt-1 font-medium">Account</span>
          </button>
        </div>
      </nav>
    </>
  );
};

export default Sidebar;